import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useStatus } from '../context/StatusContext';
import { useDataUpdates } from '../hooks/useDataUpdates';
import { useDataCache } from '../hooks/useDataCache';

import { cacheManager } from '../hooks/GlobalCacheManager';
import { EntityCellRenderer, NetworkCellRenderer, NodeStatusCellRenderer } from '../utils/metrics';
import { StatusEvaluator } from '../context/StatusEvaluator';
import { NODE_THRESHOLDS, EXTERNAL_VALIDATION_THRESHOLDS } from '../config/thresholds';
import LoadingAnimation from '../components/loadingAnimation';
import '../styles/Nodes.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getApiBase } from '../utils/apiBase';

function Nodes() {
  const { getCachedData, setCachedData } = useDataCache();
  const [nodeData, setNodeData] = useState(() => {
    try { return getCachedData('/node_info.json') || {}; } catch { return {}; }
  });

  const [nodeHeads, setNodeHeads] = useState(() => {
    try { return getCachedData('/node_heads.json') || {}; } catch { return {}; }
  });
  const [nodeDbInfo, setNodeDbInfo] = useState(() => {
    try { return getCachedData('/node_db_info.json') || {}; } catch { return {}; }
  });
  const [loading, setLoading] = useState(() => {
    try { return getCachedData('/node_info.json') ? false : true; } catch { return true; }
  });
  const [error, setError] = useState(null);
  const [displayMode, setDisplayMode] = useState('chain');
  const { updatePageStatus } = useStatus();
  const [rowData, setRowData] = useState([]);
  const gridApiRef = useRef(null);

  const fetchNodeHeads = useCallback(() => {
    const API = getApiBase();
    return fetch(`${API}/node_heads.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data === 'object') {
          setNodeHeads(data);
          setCachedData('/node_heads.json', data);
        }
        return data || {};
      })
      .catch(() => ({}));
  }, [setCachedData]);

  const fetchNodeDbInfo = useCallback(() => {
    const API = getApiBase();
    return fetch(`${API}/node_db_info.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && typeof data === 'object') {
          setNodeDbInfo(data);
          setCachedData('/node_db_info.json', data);
        }
        return data || {};
      })
      .catch(() => ({}));
  }, [setCachedData]);

  const fetchData = useCallback(() => {
    console.log('Fetching node data at:', new Date().toISOString());
    // Avoid toggling spinner on subsequent updates to prevent flicker
    setError(null);

    const API = getApiBase();
    
    // Fetch node data and fast heads/db snapshots
    Promise.all([
      fetch(`${API}/node_info.json`).then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP error! status: ${r.status}`))),
      fetchNodeHeads(),
      fetchNodeDbInfo(),
    ])
      .then(([nodeData]) => {
        console.log('Received node data:', nodeData);
        setNodeData(nodeData);
        setCachedData('/node_info.json', nodeData);
        const status = StatusEvaluator.nodes(nodeData, null);
        updatePageStatus('nodes', status);
      })

      .catch((error) => {
        console.error('Error fetching node data:', error);
        setError(error.message);
        updatePageStatus('nodes', 'red');
      })

      .finally(() => {
        setLoading(false);
      });
  }, [updatePageStatus, fetchNodeHeads, fetchNodeDbInfo]);

  useEffect(() => {
    console.log('Nodes component mounted, performing initial data fetch');
    // Register endpoints for SSE-driven warming and prewarm cache
    try {
      cacheManager.registerEndpoints('nodes', [
        '/node_info.json',
        '/node_heads.json',
        '/node_db_info.json',
        '/external_chain_heights.json'
      ]);
      // Prewarm immediately to improve first paint
      cacheManager.prewarmForPageId('nodes').catch(() => {});
    } catch {}

    // Fast path: render immediately from cache if present (do not block on freshness check)
    (async () => {
      try {
        const { globalCache } = require('../hooks/useDataCache');
        const cachedNodeInfo = globalCache.get('/node_info.json');
        const cachedDbInfo = globalCache.get('/node_db_info.json');
        if (cachedNodeInfo && typeof cachedNodeInfo === 'object') {
          setNodeData(cachedNodeInfo);
          setLoading(false);
        }
        if (cachedDbInfo && typeof cachedDbInfo === 'object') {
          setNodeDbInfo(cachedDbInfo);
        }
        // If both caches available, update page status immediately
        try {
          if (cachedNodeInfo) {
            const st = StatusEvaluator.nodes(cachedNodeInfo, null);
            updatePageStatus('nodes', st);
          }
        } catch {}
      } catch {}

      fetchData();
    })();
  }, [fetchData, updatePageStatus]);

  // Build grid rows for either mode; placed before SSE handler to avoid TDZ when invoked early.
  // Chain tab draws exclusively from node_info.json. Database tab unions entities/networks
  // from node_info.json AND node_db_info.json so DB-only hosts (e.g. ENV_BACKUP, which runs
  // the FDC verifier DBs but no blockchain nodes) still render on the Database tab without
  // appearing on the Chain tab.
  const buildRows = useCallback((data, mode, overrideDbInfo = null) => {
    const rows = [];
    const sourceDb = overrideDbInfo || nodeDbInfo;
    const safeData = (data && typeof data === 'object') ? data : {};
    const safeDb = (sourceDb && typeof sourceDb === 'object') ? sourceDb : {};
    const allowedNetworksForDatabase = ['BITCOIN', 'DOGECOIN', 'XRPL'];

    const byEntity = new Map();
    const addNetworks = (entity, networks, filter = null) => {
      if (!entity || entity === 'timestamp' || !networks || typeof networks !== 'object') return;
      if (!byEntity.has(entity)) byEntity.set(entity, new Set());
      const bucket = byEntity.get(entity);
      Object.keys(networks).forEach((n) => {
        if (!filter || filter.includes(n)) bucket.add(n);
      });
    };

    Object.entries(safeData).forEach(([entity, networks]) => addNetworks(entity, networks));
    if (mode === 'database') {
      Object.entries(safeDb).forEach(([entity, networks]) => addNetworks(entity, networks, allowedNetworksForDatabase));
    }

    byEntity.forEach((networkSet, entity) => {
      networkSet.forEach((network) => {
        const nodeInfo = ((safeData[entity] || {})[network]) || null;

        if (mode === 'database' && !allowedNetworksForDatabase.includes(network)) return;
        // Chain tab requires a node_info entry — DB-only entities have none and are skipped.
        if (mode === 'chain' && !nodeInfo) return;

        const { status = 'error', chain = null, database = null, type = 'FDC', ip_address = null, gc_name = null, simple_health = 'Unknown' } = nodeInfo || {};
        const rawDatum = mode === 'chain' ? chain : database;
        let datum = { ...(rawDatum || {}) };

        if (mode === 'database') {
          try {
            const light = ((safeDb[entity] || {})[network]) || null;
            if (light && typeof light === 'object') {
              datum = { ...datum, ...light };
            }
          } catch {}
        }

        const statusForMode = mode === 'database' ? (datum?.status ?? status) : status;
        const rowData = {
          entity,
          network,
          status: statusForMode,
          type,
          ip_address,
          gc_name,
          simple_health,
          external_validation: nodeInfo?.external_validation,
          ...(datum || {}),
        };
        if (mode === 'database' && chain?.latest_block) {
          rowData.chain_latest_block = chain.latest_block;
        }
        rows.push(rowData);
      });
    });

    rows.sort((a, b) => {
      const e = String(a.entity || '').localeCompare(String(b.entity || ''));
      if (e !== 0) return e;
      return String(a.network || '').localeCompare(String(b.network || ''));
    });
    return rows;
  }, [nodeDbInfo]);

  // On any SSE update, warm caches via GlobalCacheManager, hydrate immediately from cache, then fetch fresh
  const checkAndFetchOnSse = useCallback(async () => {
    if (displayMode !== 'database') {
      fetchData();
      return;
    }
    try {
      if (cacheManager.handleSSEUpdate) {
        await cacheManager.handleSSEUpdate({ source: 'nodes' });
      }
    } catch {}

    try {
      const cachedInfo = getCachedData('/node_info.json');
      if (cachedInfo && typeof cachedInfo === 'object') setNodeData(cachedInfo);
      const cachedHeads = getCachedData('/node_heads.json');
      if (cachedHeads && typeof cachedHeads === 'object') setNodeHeads(cachedHeads);
      const cachedDb = getCachedData('/node_db_info.json');
      if (cachedDb && typeof cachedDb === 'object') setNodeDbInfo(cachedDb);
    } catch {}
    await Promise.all([fetchNodeDbInfo(), fetchNodeHeads()]);
  }, [displayMode, fetchData, getCachedData, fetchNodeDbInfo, fetchNodeHeads]);

  useDataUpdates(checkAndFetchOnSse);

  // Function to calculate external validation for a node using fast heads snapshot, computed in FE
  const calculateExternalValidation = useCallback((network, nodeData) => {
    const entityKey = nodeData?.entity;
    const networkKey = (network || '').toString().toUpperCase();
    const chainMapping = {
      'BITCOIN': 'BTC',
      'DOGECOIN': 'DOGE',
      'ETHEREUM': 'ETH',
      'FLARE': 'FLR',
      'FLARE_OBS': 'FLR',
      'FLARE_OBS2': 'FLR',
      'FLARE_VAL': 'FLR',
      'FLARE_VAL2': 'FLR',
      'SONGBIRD': 'SGB',
      'SONGBIRD_OBS': 'SGB',
      'SONGBIRD_OBS2': 'SGB',
      'XRPL': 'XRPL',
    };
    const extKey = chainMapping[networkKey] || 'DEFAULT';

    // Helper to compute status from heights
    const compute = (extHeight, nodeHeight) => {
      const ext = typeof extHeight === 'string' ? parseInt(extHeight, 10) : extHeight;
      const nodeH = typeof nodeHeight === 'string' ? parseInt(nodeHeight, 10) : nodeHeight;
      if (!Number.isFinite(ext) || !Number.isFinite(nodeH)) return null;
      const blocksBehind = ext - nodeH;
      const th = EXTERNAL_VALIDATION_THRESHOLDS[extKey] || EXTERNAL_VALIDATION_THRESHOLDS.DEFAULT;
      let status;
      if (blocksBehind <= 0) status = 'synced';
      else if (blocksBehind <= th.GREEN_MAX) status = 'synced';
      else if (blocksBehind <= th.AMBER_MAX) status = 'behind';
      else status = 'significantly_behind';
      return { status, blocks_behind: blocksBehind, external_height: ext, node_height: nodeH };
    };

    // Prefer fast heads snapshot if available
    const fast = nodeHeads?.[entityKey]?.[networkKey];
    if (fast && (fast.external_height != null || fast.node_height != null)) {
      const res = compute(fast.external_height, fast.node_height);
      if (res) return res;
    }
    // Fallback to embedded heights if present
    const embedded = nodeData?.external_validation;
    if (embedded && (embedded.external_height != null || embedded.node_height != null)) {
      const res = compute(embedded.external_height, embedded.node_height);
      if (res) return res;
    }
    return { status: 'no_heads_data', blocks_behind: null, external_height: null };
  }, [nodeHeads]);

  // Update rowData for Chain tab via effect; Database tab rows are applied separately.
  useEffect(() => {
    if (displayMode !== 'chain') return;
    const nextRows = buildRows(nodeData, 'chain');
    const prev = rowData;
    const changed = JSON.stringify(prev) !== JSON.stringify(nextRows);
    if (changed) {
      setRowData(nextRows);
      try {
        if (gridApiRef.current) {
          setTimeout(() => { try { gridApiRef.current.refreshCells({ force: true }); } catch {} }, 0);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeData, nodeHeads, displayMode, buildRows]);

  useEffect(() => {
    if (displayMode !== 'database') return;
    const nextRows = buildRows(nodeData, 'database');
    const prev = rowData;
    const changed = JSON.stringify(prev) !== JSON.stringify(nextRows);
    if (changed) {
      setRowData(nextRows);
      try {
        if (gridApiRef.current) {
          setTimeout(() => { try { gridApiRef.current.refreshCells({ force: true }); } catch {} }, 0);
        }
      } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeData, nodeDbInfo, displayMode, buildRows]);

  const HealthStatusCellRenderer = useCallback((params) => {
    const row = params.data || {};
    const validation = calculateExternalValidation(row?.network, row);
    const statusLower = (row.status || '').toString().toLowerCase();
    
    let color = '#FFA500'; // default amber
    
    // If the node reports SYNCING (or similar transitional states), force red regardless of external validation
    if (statusLower === 'syncing' || statusLower === 'indexing') {
      color = '#DC3545';
    } else if (validation && validation.status !== 'no_heads_data' && validation.status !== 'no_node_height') {
      if (validation.status === 'synced') {
        color = '#008000'; // Green
      } else if (validation.status === 'behind') {
        color = '#FFA500'; // Amber
      } else if (validation.status === 'significantly_behind') {
        color = '#DC3545'; // Red
      }
    } else {
      // Fallback to original status logic
      const statusStr = (row.status || '').toString().toUpperCase();
      if (statusStr === 'SYNCED') color = '#008000';
      else if (statusStr === 'BEHIND') color = '#FFA500';
      else color = '#DC3545'; // SYNCING / errors
    }
    
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
        <span style={{ color, fontSize: '20px' }}>●</span>
      </div>
    );
  }, [calculateExternalValidation]);

  const getColumnDefs = useCallback(() => {
    const formatDbTime = (v) => {
      if (v === null || v === undefined) return v;
      const s = typeof v === 'number' ? String(v) : String(v).trim();
      if (/^\d+$/.test(s)) {
        const n = parseInt(s, 10);
        const ms = n > 1e12 ? n : n * 1000;
        const d = new Date(ms);
        const pad = (x) => String(x).padStart(2, '0');
        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
      }
      return s;
    };
    const commonColumns = [
      {
        headerName: '',
        field: 'simple_health',
        minWidth: 30,
        flex: 0.5,
        cellRenderer: HealthStatusCellRenderer,
        filter: '',
        sortable: false,
        headerComponentParams: {
          template:
            '<div class="health-header">' +
            '<img src="/assets/cloud.svg" alt="Node Status" title="Node Connection Status"/>' +
            '</div>'
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 120,
        flex: 1,
        filter: 'agTextColumnFilter',
        valueGetter: params => {
          const originalStatus = params.data?.status;
          const validation = calculateExternalValidation(params.data?.network, params.data);
          
          // If we have external validation data, use it to override the status
          if (validation && validation.status !== 'no_external_data' && validation.status !== 'no_node_height') {
            if (validation.status === 'significantly_behind') {
              return `${validation.blocks_behind} BEHIND`;
            } else if (validation.status === 'behind') {
              return `${validation.blocks_behind} BEHIND`;
            } else if (validation.status === 'synced') {
              return 'SYNCED';
            }
          }
          
          return originalStatus;
        },
        cellRenderer: NodeStatusCellRenderer
      },
      {
        field: 'entity',
        headerName: 'Entity',
        minWidth: 140,
        flex: 1,
        cellRenderer: EntityCellRenderer,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'network',
        headerName: 'Network',
        minWidth: 130,
        flex: 1,
        valueGetter: params => {
          const networkKey = params.data.network;
          if (networkKey && networkKey.includes('_')) {
            return networkKey.split('_')[0];
          }
          return networkKey;
        },
        cellRenderer: NetworkCellRenderer,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'type',
        headerName: 'Type',
        minWidth: 100,
        flex: 1,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'ip_address',
        headerName: 'IP',
        minWidth: 110,
        flex: 1,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'gc_name',
        headerName: 'GC NAME',
        minWidth: 210,
        flex: 1,
        filter: 'agTextColumnFilter',
      }
    ];

    const chainColumns = [
      {
        field: 'external_validation',
        headerName: 'External Height',
        minWidth: 140,
        flex: 1,
        filter: 'agTextColumnFilter',
        valueGetter: params => {
          const validation = calculateExternalValidation(params.data?.network, params.data);
          if (!validation || validation.status === 'no_heads_data') return 'No heads data';
          if (validation.status === 'no_node_height') return 'No node height';
          
          // Show the external block height without comma formatting
          const externalHeight = validation.external_height;
          if (externalHeight !== null && externalHeight !== undefined) {
            return externalHeight.toString();
          }
          
          return 'Unknown';
        },
      },
      {
        headerName: 'Latest Block',
        valueGetter: params => {
          const entity = params.data.entity;
          const key = (params.data.network || '').toString().toUpperCase();
          const fast = nodeHeads?.[entity]?.[key];
          const prefer = (fast && typeof fast.node_height === 'number') ? fast.node_height : params.data.latest_block;
          const n = typeof prefer === 'string' ? parseInt(prefer, 10) : prefer;
          return isNaN(n) ? null : n;
        },
        minWidth: 120,
        flex: 1,
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Earliest Block',
        valueGetter: params => {
          const v = params.data.first_block ?? params.data.earliest_block;
          const n = typeof v === 'string' ? parseInt(v, 10) : v;
          return isNaN(n) ? null : n;
        },
        minWidth: 140,
        flex: 1,
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Total Blocks',
        valueGetter: params => {
          const entity = params.data.entity;
          const key = (params.data.network || '').toString().toUpperCase();
          const fast = nodeHeads?.[entity]?.[key];
          const latestRaw = (fast && typeof fast.node_height === 'number') ? fast.node_height : params.data.latest_block;
          const earliestRaw = params.data.first_block ?? params.data.earliest_block;
          const latest = typeof latestRaw === 'string' ? parseInt(latestRaw, 10) : latestRaw;
          const earliest = typeof earliestRaw === 'string' ? parseInt(earliestRaw, 10) : earliestRaw;
          if (isNaN(latest) || isNaN(earliest)) return null;
          const total = latest - earliest;
          return total >= 0 ? total : null;
        },
        minWidth: 140,
        flex: 1,
        filter: 'agNumberColumnFilter',
      },
      {
        headerName: 'Difference',
        valueGetter: params => {
          const entity = params.data.entity;
          const key = (params.data.network || '').toString().toUpperCase();
          const fast = nodeHeads?.[entity]?.[key];
          const validation = calculateExternalValidation(params.data?.network, params.data);
          const extRaw = (fast && fast.external_height != null) ? fast.external_height : (validation?.external_height ?? null);
          const latestRaw = (fast && fast.node_height != null) ? fast.node_height : params.data.latest_block;
          const ext = typeof extRaw === 'string' ? parseInt(extRaw, 10) : extRaw;
          const latest = typeof latestRaw === 'string' ? parseInt(latestRaw, 10) : latestRaw;
          if (!Number.isFinite(ext) || !Number.isFinite(latest)) return null;
          const diff = Math.abs(ext - latest);
          return diff;
        },
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'range',
        headerName: 'Range',
        minWidth: 120,
        flex: 1,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'earliest_block_time',
        headerName: 'Earliest Block Time',
        minWidth: 180,
        flex: 1.5,
        filter: 'agTextColumnFilter',
      },
      {
        field: 'latest_block_time',
        headerName: 'Latest Block',
        minWidth: 150,
        flex: 2,
        filter: 'agTextColumnFilter'
      },
      {
        field: 'version',
        headerName: 'Version',
        minWidth: 80,
        flex: 1,
        filter: 'agTextColumnFilter',
      },
    ];

    const databaseColumns = [
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 120,
        flex: 1,

        filter: 'agTextColumnFilter',
        valueGetter: params => {
          const networkKey = (params.data?.network || '').toString().toUpperCase();
          const dbLatest = params.data?.latest_block;
          const nodeHeight = params.data?.node_height;

          if (typeof dbLatest === 'number' && typeof nodeHeight === 'number') {
            const blocksBehind = nodeHeight - dbLatest;
            if (blocksBehind <= 0) {
              return networkKey.startsWith('XRPL') ? 'INDEXED' : 'SYNCED';
            }
            return blocksBehind === 1 ? '1 BEHIND' : `${blocksBehind} BEHIND`;
          }

          // Fallback to raw status if numbers missing
          const raw = (params.data?.status || '').toString();
          return raw;
        },
        cellRenderer: NodeStatusCellRenderer
      },
      {
        field: 'earliest_block',
        headerName: 'Earliest',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'latest_block',
        headerName: 'Latest',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'total_blocks',
        headerName: 'Total',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
      },
      {
        field: 'external_validation',
        headerName: 'Node Height',
        minWidth: 140,
        flex: 1,
        filter: 'agTextColumnFilter',
        valueGetter: params => {
          const live = params.data?.node_height;
          if (typeof live === 'number') return live;
          return 'Unknown';
        },
      },
      
      {
        field: 'blocks_remaining',
        headerName: 'Remaining',
        minWidth: 100,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueGetter: params => {
          const dbLatest = params.data?.latest_block;
          const nodeHeight = params.data?.node_height;
          if (typeof dbLatest === 'number' && typeof nodeHeight === 'number') {
            const diff = nodeHeight - dbLatest;
            return diff >= 0 ? diff : 0;
          }
          return params.data?.blocks_remaining ?? null;
        },
      },
      {
        field: 'indexing_progress',
        headerName: 'Progress',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
      },

            {
        field: 'earliest_db_time',
        headerName: 'Earliest DB Time',
        minWidth: 200,
        flex: 2,
        filter: 'agTextColumnFilter',
        valueGetter: params => formatDbTime(params.data?.earliest_db_time),
      },
      {
        field: 'latest_db_time',
        headerName: 'Latest DB Time',
        minWidth: 200,
        flex: 2,
        filter: 'agTextColumnFilter',
        valueGetter: params => formatDbTime(params.data?.latest_db_time),
        cellStyle: params => {
          const value = params.value;
          const networkKey = params.data.network;
          if (typeof value !== 'string' || value === "Unknown" || value === "N/A") {
            return null;
          }
          try {
            const dbTimeMs = new Date(value.replace(' ', 'T') + 'Z').getTime();
            const currentTimeMs = Date.now();
            const diffMinutes = (currentTimeMs - dbTimeMs) / (1000 * 60);

            let thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.DEFAULT;
            const upperNetworkKey = networkKey.toUpperCase();

            if (upperNetworkKey === 'BITCOIN' && NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.BTC) {
              thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.BTC;
            } else if (NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES[upperNetworkKey]) { 
              thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES[upperNetworkKey];
            } else if (upperNetworkKey.startsWith('DOGE') && NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.DOGE) {
              thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.DOGE;
            } else if (upperNetworkKey.startsWith('XRPL') && NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.XRPL) {
              thresholdConfig = NODE_THRESHOLDS.LATEST_DB_TIME_MINUTES.XRPL;
            } 
            
            if (diffMinutes > thresholdConfig.RED_ABOVE) {
              return { color: '#DC3545' };
            } else if (diffMinutes > thresholdConfig.AMBER_ABOVE) {
              return { color: '#FFA500' };
            }
          } catch (e) {
            console.error("Error parsing date for latest_db_time:", value, e);
            return null;
          }
          return null;
        }
      },
      {
        field: 'database_size',
        headerName: 'DB Size',
        minWidth: 150,
        flex: 1,
        filter: 'agTextColumnFilter',
      },
      
    ];

    return displayMode === 'chain'
      ? [...commonColumns, ...chainColumns]
      : [
          ...commonColumns.filter(col => !['simple_health', 'type', 'ip_address', 'gc_name', 'status'].includes(col.field)),
          ...databaseColumns
        ];
  }, [displayMode, calculateExternalValidation, HealthStatusCellRenderer, nodeHeads]);

  const memoColumnDefs = useMemo(() => getColumnDefs(), [getColumnDefs]);

  const getRowId = useCallback((params) => {
    const row = params?.data || {};
    return `${row.entity || ''}-${row.network || ''}`;
  }, []);

  const handleTabChange = (mode) => {
    if (mode === displayMode) return;
    setDisplayMode(mode);
  };

  // When switching to Database tab, hydrate immediately from cache and fetch fresh DB/light snapshots
  useEffect(() => {
    if (displayMode !== 'database') return;
    try {
      const { globalCache } = require('../hooks/useDataCache');
      const cachedDb = globalCache.get('/node_db_info.json');
      const cachedHeads = globalCache.get('/node_heads.json');
      if (cachedDb && typeof cachedDb === 'object') setNodeDbInfo(cachedDb);
      if (cachedHeads && typeof cachedHeads === 'object') setNodeHeads(cachedHeads);
    } catch {}
    // Kick targeted refreshes so DB tab doesn't wait for the general sweep
    fetchNodeDbInfo();
    fetchNodeHeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayMode]);

  if (loading) {
    return <LoadingAnimation />;
  }

  if (error) {
    return <div className="error-message">Error loading nodes: {error}</div>;
  }


  const defaultColDef = {
    sortable: true,
    resizable: true,
  };

  const gridOptions = {
    suppressScrollOnNewData: true,
    suppressHorizontalScroll: true,
    domLayout: 'normal',
    getRowHeight: () => 50,
  };

  // Single grid instance to avoid remounts on each update

  return (
    <div className="main-wrapper nodes-page">
      <div className="nodes-wrapper">
        <div className="nodes-content-wrapper">
          <div className="toggle-wrapper nodes-toggle-wrapper">
            <div className="network-toggle nodes-network-toggle">
              <button
                className={`nodes-toggle-button ${displayMode === 'chain' ? 'active' : ''}`}
                onClick={() => handleTabChange('chain')}
              >
                Chain
              </button>
              <button
                className={`nodes-toggle-button ${displayMode === 'database' ? 'active' : ''}`}
                onClick={() => handleTabChange('database')}
              >
                Database
              </button>
              <div className="slider nodes-slider"></div>
            </div>
          </div>
          
          <div className="node-grid-wrapper">
            <div className="ag-theme-alpine-dark custom-dark-theme">
              <AgGridReact
                columnDefs={memoColumnDefs}
                rowData={rowData}
                defaultColDef={defaultColDef}
                gridOptions={gridOptions}
                deltaRowDataMode={displayMode === 'chain'}
                getRowId={getRowId}
                suppressAnimationFrame={true}
                suppressRowClickSelection={true}
                onGridReady={(params) => {
                  gridApiRef.current = params.api;
                  params.api.sizeColumnsToFit();
                }}
                onFirstDataRendered={(params) => {
                  params.api.sizeColumnsToFit();
                  try { if (gridApiRef.current) { setTimeout(() => { try { gridApiRef.current.refreshCells({ force: true }); } catch {} }, 0); } } catch {}
                }}
                domLayout="normal"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Nodes;